import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setArtistNotify } from '../redux/thunk/followedArtistsThunk';
import { setTrackNotify } from '../redux/thunk/likesThunk';
import { setLabelNotify } from '../redux/thunk/followedLabelsThunk';
import { entityId } from '../utils/entityId';
import MixpediaHint from './DJPal/MixpediaHint';
import { getSeenHints, markHintSeen } from '../utils/mixpediaHints';
import { track as track_ } from '../utils/analytics';

// The notification bell — the OTHER half of the follow/like split (Aug 2026).
// Follow/like = save + feed personalization; this bell is the only thing that
// subscribes to notifications. It renders only next to an existing follow/like
// (the server refuses a bell without the row) and defaults to off.
//
// entityType 'artist': entity = { name | artist_name }.
// entityType 'track':  entity = a track object ({ artist, title, ...ids }).
// entityType 'label':  entity = { publicId, name } (a followed record label, keyed by public id).
// `nudge` enables the one-time post-follow/post-like hint that teaches the
// bell (mount it on at most one bell per page — the header one).
const NotifyBellButton = React.forwardRef(({ entityType, entity, prominent = false, from, nudge = false }, ref) => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const followedSet = useSelector(state => state.followedArtists.followedSet);
    const artistNotifySet = useSelector(state => state.followedArtists.notifySet);
    const likedSet = useSelector(state => state.likes.likedSet);
    const likedTracks = useSelector(state => state.likes.likedTracks);
    const trackNotifySet = useSelector(state => state.likes.notifySet);
    const labelFollowedSet = useSelector(state => state.followedLabels?.followedSet || {});
    const labelNotifySet = useSelector(state => state.followedLabels?.notifySet || {});

    const isLabel = entityType === 'label';
    const labelId = isLabel ? (entity?.publicId || entity?.label_public_id || null) : null;
    const isArtist = entityType === 'artist';
    const artistName = isArtist ? (entity?.name || entity?.artist_name) : null;

    // Track identity: match the LIKED row id-first (same rule as LikeButton —
    // page text can drift from how the track was liked), and toggle using the
    // liked row's own artist/title so the server UPDATE hits the stored row.
    const pid = !isArtist && !isLabel ? entityId(entity) : null;
    const likedRow = !isArtist && !isLabel && pid
        ? (likedTracks || []).find((t) => entityId(t) === pid)
        : null;
    const trackArtist = likedRow?.artist ?? entity?.artist;
    const trackTitle = likedRow?.title ?? entity?.title;
    const trackKey = trackArtist && trackTitle ? `${trackArtist}|||${trackTitle}` : null;

    const isSaved = isLabel ? (labelId ? !!labelFollowedSet[labelId] : false) : isArtist
        ? (artistName ? !!followedSet[artistName] : false)
        : !!(likedRow || (trackKey && likedSet[trackKey]));
    const isOn = isLabel ? (labelId ? !!labelNotifySet[labelId] : false) : isArtist
        ? (artistName ? !!artistNotifySet[artistName] : false)
        : (trackKey ? !!trackNotifySet[trackKey] : false);

    // Artwork for the toggle's confirmation card (the thunk falls back to the
    // loaded follow/like row when the mount doesn't know it).
    const artwork = isArtist || isLabel
        ? (entity?.artworkUrl || entity?.artwork || null)
        : (likedRow?.artwork || entity?.artwork || entity?.artworkUrl || null);

    const toggle = useCallback((fromOverride) => {
        if (isLabel) {
            dispatch(setLabelNotify({ labelPublicId: labelId, name: entity?.name || entity?.label_name, notify: !isOn, from: fromOverride || from, artwork }));
        } else if (isArtist) {
            dispatch(setArtistNotify({ artistName, notify: !isOn, from: fromOverride || from, artwork }));
        } else {
            dispatch(setTrackNotify({ artist: trackArtist, title: trackTitle, notify: !isOn, from: fromOverride || from, artwork }));
        }
    }, [dispatch, isArtist, isLabel, labelId, entity, artistName, trackArtist, trackTitle, isOn, from, artwork]);

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isAuthenticated || !isSaved) return;
        toggle();
    }, [isAuthenticated, isSaved, toggle]);

    // One-time nudge: when the save state flips on DURING this session (the
    // user just followed/liked right here), point at the bell once.
    const hintId = isLabel ? 'bell_label' : isArtist ? 'bell_artist' : 'bell_track';
    const prevSavedRef = useRef(isSaved);
    const [showNudge, setShowNudge] = useState(false);
    const innerRef = useRef(null);
    const btnRef = ref || innerRef;

    useEffect(() => {
        const was = prevSavedRef.current;
        prevSavedRef.current = isSaved;
        if (!nudge || !isSaved || was || isOn) return;
        if (getSeenHints().includes(hintId)) return;
        setShowNudge(true);
        track_('notify_nudge_shown', { entity_type: entityType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSaved]);

    // The bell segment slides out from under the Like/Follow pill when the
    // entity becomes saved after mount (a click, or the async follow/like
    // check resolving), and back under it on unsave — the header cluster is
    // right-anchored, so an instant mount/unmount jerked everything sideways
    // in one frame. Derived during render so the first committed frame
    // already carries .entering / .leaving.
    const [prevSavedForEnter, setPrevSavedForEnter] = useState(isSaved);
    const [entering, setEntering] = useState(false);
    const [leaving, setLeaving] = useState(false);
    if (prevSavedForEnter !== isSaved) {
        setPrevSavedForEnter(isSaved);
        setEntering(isSaved);
        setLeaving(!isSaved);
    }
    // Stay mounted through the slide-out, then unmount. Desktop capsules run
    // bell-seg-out and unmount on its animationend (a fixed timer raced the
    // animation); mobile has no slide, so its brief fade window ends on a
    // timer. The long fallback keeps a leaving bell from ever sticking.
    useEffect(() => {
        if (!leaving) return undefined;
        const el = btnRef.current;
        const sliding = !!el && getComputedStyle(el).animationName === 'bell-seg-out';
        const t = setTimeout(() => setLeaving(false), sliding ? 1500 : 220);
        return () => clearTimeout(t);
    }, [leaving, btnRef]);

    if (!isAuthenticated || (!isSaved && !leaving)) return null;
    if (isLabel ? !labelId : isArtist ? !artistName : !trackKey) return null;

    // Tooltips say WHAT the bell notifies about, not just that it toggles —
    // "turn on notifications" told users nothing about what they'd get.
    // The artist bell covers TWO notification types — favourite_artist_new_mix
    // (their new mixes) and favourite_artist_track_played (a DJ plays one of
    // their tracks in a new set). Name both; "tracks they play" read as the
    // artist's own selections, which is not a notification we send.
    const offTitle = isLabel ? 'Get notified when big DJs play tracks from this label' : isArtist
        ? 'Get notified about their new mixes and when DJs play their tracks'
        : 'Get notified when a DJ plays this track in a mix';
    const onTitle = isLabel ? 'Notifying you when big DJs play this label — click to turn off' : isArtist
        ? 'Notifying you about their new mixes and when DJs play their tracks — click to turn off'
        : 'Notifying you when DJs play this track — click to turn off';

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                className={`notify-bell-btn${prominent ? ' prominent' : ''}${isOn ? ' on' : ''}${entering ? ' entering' : ''}${leaving ? ' leaving' : ''}`}
                title={isOn ? onTitle : offTitle}
                aria-label="Notifications"
                aria-pressed={isOn}
                aria-hidden={leaving || undefined}
                tabIndex={leaving ? -1 : undefined}
                onClick={handleClick}
                onTouchEnd={(e) => { e.stopPropagation(); }}
                onAnimationEnd={(e) => {
                    if (e.animationName === 'bell-seg-in') setEntering(false);
                    if (e.animationName === 'bell-seg-out') setLeaving(false);
                }}
            >
                {/* Outline = off, filled = on. Fill alone was too subtle at
                    ~15px (users read an off bell as on), so the split is
                    carried mainly by the segment's surface + color in CSS. */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill={isOn ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" fill="none" />
                </svg>
                {prominent && <span>{isOn ? 'Notifying' : 'Notify'}</span>}
            </button>
            {showNudge && (
                <MixpediaHint
                    targetRef={btnRef}
                    autoShow
                    title="Turn on the bell"
                    message={isArtist
                        ? 'Following saves them to your feed — the bell is what notifies you. Turn it on to hear about their new mixes and when DJs play their tracks.'
                        : 'Liking saves the track — the bell is what notifies you. Turn it on to hear when a DJ plays it.'}
                    actionLabel="Turn on"
                    onAction={() => {
                        if (!isOn) toggle('nudge');
                        markHintSeen(hintId);
                        setShowNudge(false);
                    }}
                    onDismiss={() => {
                        markHintSeen(hintId);
                        setShowNudge(false);
                    }}
                />
            )}
        </>
    );
});

export default NotifyBellButton;
